/**
 * Menu Configuration - Booku V2
 *
 * Struktur menu hierarkis untuk navigasi aplikasi.
 * - MainMenuItem: Menu utama di sidebar (level 1)
 * - MenuConfigItem: Item di SubMenuBar (leaf atau branch)
 */

import {
  Home,
  Users,
  BookOpen,
  FileText,
  Settings,
  LayoutDashboard,
  Building2,
  Wallet,
  Receipt,
  PieChart,
  Briefcase,
  Handshake,
  UserSquare2,
  Banknote,
  Landmark,
  CreditCard,
  Coins,
  ShoppingCart,
  ClipboardList,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeftRight,
  Package,
  DollarSign,
  Store,
  Calendar,
  Calculator,
  FileCheck,
  Heart,
  Shield,
  UsersRound,
} from 'lucide-react';
import {
  LevelUser_09_SuperUser,
  LevelUser_03_Direktur,
} from '@/modules/data/user/types';

// =============================================================================
// TYPES
// =============================================================================

/** Item leaf - memiliki path ke halaman */
export interface MenuLeafItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  path: string;
  /** Level minimum untuk melihat menu ini */
  minLevel?: number;
  /** Menu hanya tampil jika perusahaan adalah Dagang */
  requiresDagang?: boolean;
  /** Menu hanya tampil jika perusahaan adalah Manufaktur */
  requiresManufaktur?: boolean;
  /** Menu hanya tampil jika perusahaan adalah Jasa */
  requiresJasa?: boolean;
  /** Menu hanya tampil di Tahun Buku Normal */
  requiresTahunBukuNormal?: boolean;
  /** Menu hanya tampil di Tahun Buku Lampau (tahun pertama) */
  requiresTahunBukuLampau?: boolean;
}

/** Item branch - memiliki children (sub-level) */
export interface MenuBranchItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  children: MenuConfigItem[];
  /** Access control di level branch */
  minLevel?: number;
  requiresDagang?: boolean;
  requiresManufaktur?: boolean;
  requiresJasa?: boolean;
  requiresTahunBukuNormal?: boolean;
  requiresTahunBukuLampau?: boolean;
}

/** Item di SubMenuBar bisa leaf atau branch */
export type MenuConfigItem = MenuLeafItem | MenuBranchItem;

/** Menu utama di sidebar */
export interface MainMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Jika path ada, ini adalah menu leaf (langsung buka halaman) */
  path?: string;
  /** Jika children ada, buka SubMenuBar */
  children?: MenuConfigItem[];
  /** Access control */
  minLevel?: number;
  requiresDagang?: boolean;
  requiresManufaktur?: boolean;
  requiresJasa?: boolean;
  requiresTahunBukuNormal?: boolean;
  requiresTahunBukuLampau?: boolean;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

export function isMenuLeaf(item: MenuConfigItem): item is MenuLeafItem {
  return 'path' in item && typeof item.path === 'string';
}

export function isMenuBranch(item: MenuConfigItem): item is MenuBranchItem {
  return 'children' in item && Array.isArray(item.children);
}

// =============================================================================
// MENU CONFIGURATION
// =============================================================================

export const mainMenuItems: MainMenuItem[] = [
  // -------------------------------------------------------------------------
  // BERANDA - Menu leaf (langsung buka halaman)
  // -------------------------------------------------------------------------
  {
    id: 'beranda',
    label: 'Beranda',
    icon: <Home size={20} />,
    path: '/',
  },

  // -------------------------------------------------------------------------
  // TUTUP BUKU TAHUNAN — tutup tahun aktif & buka tahun berikutnya (+ Reset pengujian).
  // Tampil di LAMPAU (tutup buku) & NORMAL (reset).
  // -------------------------------------------------------------------------
  {
    id: 'tutup-buku-tahunan',
    label: 'Tutup Buku Tahunan',
    icon: <Calendar size={20} />,
    path: '/tutup-buku-tahunan',
  },

  // -------------------------------------------------------------------------
  // DATA MASTER
  // -------------------------------------------------------------------------
  {
    id: 'data',
    label: 'Data Master',
    icon: <LayoutDashboard size={20} />,
    children: [
      {
        id: 'data-user',
        label: 'Data User',
        icon: <Users size={18} />,
        path: '/users',
        minLevel: LevelUser_09_SuperUser,
      },
      {
        id: 'data-coa',
        label: 'Data COA',
        icon: <BookOpen size={18} />,
        path: '/data/coa',
        minLevel: LevelUser_03_Direktur,
      },
      {
        id: 'data-lawan-transaksi',
        label: 'Lawan Transaksi',
        icon: <Handshake size={18} />,
        path: '/data/lawan-transaksi',
        minLevel: LevelUser_03_Direktur,
      },
      {
        id: 'data-toko',
        label: 'Data Toko',
        icon: <Building2 size={18} />,
        path: '/data/toko',
        requiresDagang: true,
      },
      {
        id: 'data-karyawan',
        label: 'Data Karyawan',
        icon: <UserSquare2 size={18} />,
        path: '/data/karyawan',
        minLevel: LevelUser_03_Direktur,
      },
      {
        id: 'data-pemegang-saham',
        label: 'Pemegang Saham',
        icon: <Briefcase size={18} />,
        path: '/data/pemegang-saham',
        minLevel: LevelUser_03_Direktur,
      },
      {
        id: 'data-project',
        label: 'Data Project',
        icon: <ClipboardList size={18} />,
        path: '/data/project',
        minLevel: LevelUser_03_Direktur,
      },
    ],
  },

  // -------------------------------------------------------------------------
  // DATA AWAL
  // Menu ini HANYA muncul di Tahun Buku Lampau (tahun pertama)
  // Digunakan untuk input saldo awal hutang, piutang, dan asset
  // UserControl sama dengan Buku Pengawasan (reusable)
  // -------------------------------------------------------------------------
  {
    id: 'data-awal',
    label: 'Data Awal',
    icon: <Calendar size={20} />,
    requiresTahunBukuLampau: true,
    children: [
      // ─────────── Validasi Saldo Awal (item #1) ───────────
      // Dashboard agregator Bar Saldo seluruh Data Awal.
      // Lihat .claude/docs/Booku v2/buku-pengawasan/validasi-saldo-awal.md
      {
        id: 'data-awal-validasi-saldo-awal',
        label: 'Validasi Saldo Awal',
        icon: <FileText size={16} />,
        path: '/data-awal/validasi-saldo-awal',
      },
      // ─────────── HUTANG ───────────
      {
        id: 'data-awal-hutang',
        label: 'Hutang',
        icon: <ArrowUpCircle size={18} />,
        children: [
          // Urutan berdasarkan KodeTautanCOA (termuda → tertua)
          // Hutang Usaha (COA 21101) - Single-Tab Host (10 mode via tab internal, sama dengan Buku Pengawasan)
          { id: 'data-awal-hutang-usaha', label: 'Hutang Usaha', icon: <Receipt size={16} />, path: '/data-awal/hutang/usaha' },
          // Hutang Pihak Ketiga (COA 21802)
          { id: 'data-awal-hutang-pihak-ketiga', label: 'Hutang Pihak Ketiga', icon: <Handshake size={16} />, path: '/data-awal/hutang/pihak-ketiga' },
          // Hutang Karyawan (COA 21808)
          { id: 'data-awal-hutang-karyawan', label: 'Hutang Karyawan', icon: <UserSquare2 size={16} />, path: '/data-awal/hutang/karyawan' },
          // Hutang Leasing (COA 22100)
          { id: 'data-awal-hutang-leasing', label: 'Hutang Leasing', icon: <CreditCard size={16} />, path: '/data-awal/hutang/leasing' },
          // Hutang Bank (COA 23100)
          { id: 'data-awal-hutang-bank', label: 'Hutang Bank', icon: <Landmark size={16} />, path: '/data-awal/hutang/bank' },
          // Hutang Pemegang Saham (COA 24100)
          { id: 'data-awal-hutang-pemegang-saham', label: 'Hutang Pemegang Saham', icon: <Users size={16} />, path: '/data-awal/hutang/pemegang-saham' },
          // Hutang Afiliasi (COA 25100)
          { id: 'data-awal-hutang-afiliasi', label: 'Hutang Afiliasi', icon: <Building2 size={16} />, path: '/data-awal/hutang/afiliasi' },
          // Hutang Pajak
          {
            id: 'data-awal-hutang-pajak',
            label: 'Hutang Pajak',
            icon: <Calculator size={16} />,
            children: [
              { id: 'data-awal-hutang-pph-21', label: 'PPh Pasal 21', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-21' },
              { id: 'data-awal-hutang-pph-23', label: 'PPh Pasal 23', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-23' },
              { id: 'data-awal-hutang-pph-42', label: 'PPh Pasal 4(2)', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-42' },
              { id: 'data-awal-hutang-pph-25', label: 'PPh Pasal 25', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-25' },
              { id: 'data-awal-hutang-pph-26', label: 'PPh Pasal 26', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-26' },
              { id: 'data-awal-hutang-pph-29', label: 'PPh Pasal 29', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/pph-29' },
              { id: 'data-awal-hutang-ppn', label: 'PPN', icon: <Calculator size={16} />, path: '/data-awal/hutang/pajak/ppn' },
              { id: 'data-awal-hutang-ketetapan-pajak', label: 'Ketetapan Pajak', icon: <FileCheck size={16} />, path: '/data-awal/hutang/pajak/ketetapan' },
            ],
          },
          // Gaji (Turunan Gaji)
          {
            id: 'data-awal-gaji',
            label: 'Gaji',
            icon: <Banknote size={16} />,
            children: [
              { id: 'data-awal-gaji-gaji', label: 'Gaji', icon: <Banknote size={16} />, path: '/data-awal/hutang/gaji/gaji' },
              { id: 'data-awal-gaji-bpjs-kesehatan', label: 'BPJS Kesehatan', icon: <Heart size={16} />, path: '/data-awal/hutang/gaji/bpjs-kesehatan' },
              { id: 'data-awal-gaji-bpjs-ketenagakerjaan', label: 'BPJS Ketenagakerjaan', icon: <Shield size={16} />, path: '/data-awal/hutang/gaji/bpjs-ketenagakerjaan' },
              { id: 'data-awal-gaji-koperasi', label: 'Hutang Koperasi Karyawan', icon: <UsersRound size={16} />, path: '/data-awal/hutang/gaji/koperasi' },
              { id: 'data-awal-gaji-serikat', label: 'Hutang Serikat', icon: <Users size={16} />, path: '/data-awal/hutang/gaji/serikat' },
            ],
          },
        ],
      },

      // ─────────── PIUTANG ───────────
      {
        id: 'data-awal-piutang',
        label: 'Piutang',
        icon: <ArrowDownCircle size={18} />,
        children: [
          // Urutan berdasarkan KodeTautanCOA (termuda → tertua)
          // Piutang Usaha (COA 11501) - Single-Tab Host (10 mode via tab internal, sama dengan Buku Pengawasan)
          { id: 'data-awal-piutang-usaha', label: 'Piutang Usaha', icon: <Receipt size={16} />, path: '/data-awal/piutang/usaha' },
          // Piutang Afiliasi (COA 11503)
          { id: 'data-awal-piutang-afiliasi', label: 'Piutang Afiliasi', icon: <Building2 size={16} />, path: '/data-awal/piutang/afiliasi' },
          // Piutang Pihak Ketiga (COA 11504)
          { id: 'data-awal-piutang-pihak-ketiga', label: 'Piutang Pihak Ketiga', icon: <Handshake size={16} />, path: '/data-awal/piutang/pihak-ketiga' },
          // Piutang Pemegang Saham (COA 11505)
          { id: 'data-awal-piutang-pemegang-saham', label: 'Piutang Pemegang Saham', icon: <Users size={16} />, path: '/data-awal/piutang/pemegang-saham' },
          // Piutang Karyawan (COA 11508)
          { id: 'data-awal-piutang-karyawan', label: 'Piutang Karyawan', icon: <UserSquare2 size={16} />, path: '/data-awal/piutang/karyawan' },
          // Deposit Operasional (COA 11608)
          { id: 'data-awal-deposit-operasional', label: 'Deposit Operasional', icon: <Wallet size={16} />, path: '/data-awal/piutang/deposit-operasional' },
        ],
      },

      // ─────────── PERSEDIAAN (Stock Opname) ───────────
      // Stock Opname terkait COA Persediaan (11801-11804) — Aktiva Lancar.
      // Posisi: setelah Piutang (11501-11608) dan sebelum Asset Tetap (mengikuti ascending COA).
      // Hanya tampil untuk perusahaan dengan flag manufaktur.
      { id: 'data-awal-stock-opname', label: 'Stock Opname', icon: <Package size={18} />, requiresManufaktur: true, path: '/data-awal/stock-opname' },

      // ─────────── ASSET ───────────
      {
        id: 'data-awal-asset',
        label: 'Asset',
        icon: <Briefcase size={18} />,
        children: [
          { id: 'data-awal-amortisasi-biaya', label: 'Amortisasi Biaya', icon: <PieChart size={16} />, path: '/data-awal/asset/amortisasi-biaya' },
          { id: 'data-awal-asset-tetap', label: 'Asset Tetap', icon: <Package size={16} />, path: '/data-awal/asset/asset-tetap' },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PEMBELIAN
  // Struktur mengikuti Booku Lama dengan penyesuaian pattern V2:
  // - PO & Invoice: MDM pattern (tab internal dalam 1 halaman)
  // - Surat Jalan, BAST, Retur: Single page (1 tab aplikasi)
  // - Buku Pembelian: Multi-Tab Host (tiap mode = tab aplikasi terpisah)
  // -------------------------------------------------------------------------
  {
    id: 'pembelian',
    label: 'Pembelian',
    icon: <ShoppingCart size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      // PO Pembelian - MDM pattern (8 mode dalam 1 halaman dengan tab internal)
      {
        id: 'po-pembelian',
        label: 'PO Pembelian',
        icon: <ClipboardList size={18} />,
        path: '/pembelian/po',
      },
      // Surat Jalan - Single page
      {
        id: 'surat-jalan-pembelian',
        label: 'Surat Jalan',
        icon: <FileText size={18} />,
        path: '/pembelian/surat-jalan',
      },
      // BAST - Single page
      {
        id: 'bast-pembelian',
        label: 'BAST',
        icon: <FileText size={18} />,
        path: '/pembelian/bast',
      },
      // Invoice Pembelian - MDM pattern (11 mode dalam 1 halaman dengan tab internal)
      {
        id: 'invoice-pembelian',
        label: 'Invoice Pembelian',
        icon: <Receipt size={18} />,
        path: '/pembelian/invoice',
      },
      // ─────────── Separator ───────────
      // Buku Pembelian - Single-Tab Host (1 tab aplikasi, tab internal: Lokal/Impor)
      {
        id: 'buku-pembelian',
        label: 'Buku Pembelian',
        icon: <BookOpen size={18} />,
        path: '/pembelian/buku',
      },
      // Retur Pembelian - Single page
      {
        id: 'retur-pembelian',
        label: 'Retur Pembelian',
        icon: <ArrowLeftRight size={18} />,
        path: '/pembelian/retur',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PENJUALAN
  // Struktur mengikuti Booku Lama dengan penyesuaian pattern V2:
  // - PO, Invoice, Buku: Single-Tab Host (1 tab aplikasi, mode via tab internal)
  // - Surat Jalan, BAST, Retur: Single page (1 tab aplikasi)
  // - Penjualan Eceran: Multi-Tab Host (tiap rekap = tab aplikasi terpisah)
  // -------------------------------------------------------------------------
  {
    id: 'penjualan',
    label: 'Penjualan',
    icon: <Store size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      // PO Penjualan - Single-Tab Host (6 mode dalam 1 halaman dengan tab internal)
      {
        id: 'po-penjualan',
        label: 'PO Penjualan',
        icon: <ClipboardList size={18} />,
        path: '/penjualan/po',
      },
      // Surat Jalan - Single page
      {
        id: 'surat-jalan-penjualan',
        label: 'Surat Jalan',
        icon: <FileText size={18} />,
        path: '/penjualan/surat-jalan',
      },
      // BAST - Single page
      {
        id: 'bast-penjualan',
        label: 'BAST',
        icon: <FileText size={18} />,
        path: '/penjualan/bast',
      },
      // Invoice Penjualan - Single-Tab Host (10 mode dalam 1 halaman dengan tab internal)
      {
        id: 'invoice-penjualan',
        label: 'Invoice Penjualan',
        icon: <Receipt size={18} />,
        path: '/penjualan/invoice',
      },
      // ─────────── Separator ───────────
      // Buku Penjualan - Single-Tab Host (1 tab aplikasi, tab internal: Lokal/Ekspor)
      {
        id: 'buku-penjualan',
        label: 'Buku Penjualan',
        icon: <BookOpen size={18} />,
        path: '/penjualan/buku',
      },
      // Penjualan Eceran - Multi-Tab Host (tiap rekap = tab aplikasi terpisah)
      {
        id: 'penjualan-eceran',
        label: 'Penjualan Eceran',
        icon: <Store size={18} />,
        children: [
          {
            id: 'penjualan-eceran-harian',
            label: 'Rekap Harian',
            icon: <Calendar size={16} />,
            path: '/penjualan/eceran/harian',
          },
          {
            id: 'penjualan-eceran-bulanan',
            label: 'Rekap Bulanan',
            icon: <Calendar size={16} />,
            path: '/penjualan/eceran/bulanan',
          },
          {
            id: 'penjualan-eceran-tahunan',
            label: 'Rekap Tahunan',
            icon: <Calendar size={16} />,
            path: '/penjualan/eceran/tahunan',
          },
        ],
      },
      // Retur Penjualan - Single page
      {
        id: 'retur-penjualan',
        label: 'Retur Penjualan',
        icon: <ArrowLeftRight size={18} />,
        path: '/penjualan/retur',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // TRANSAKSI
  // -------------------------------------------------------------------------
  {
    id: 'transaksi',
    label: 'Transaksi',
    icon: <Receipt size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      {
        id: 'transaksi-in',
        label: 'Transaksi IN',
        icon: <ArrowDownCircle size={18} />,
        path: '/transaksi/in',
      },
      {
        id: 'transaksi-out',
        label: 'Transaksi OUT',
        icon: <ArrowUpCircle size={18} />,
        path: '/transaksi/out',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // BUKU PENGAWASAN
  // Struktur mengikuti Booku Lama dengan penyesuaian pattern V2:
  // - Hutang Usaha/Piutang Usaha: Single-Tab Host (10 mode via tab internal)
  // - Hutang Bank-Leasing: Single-Tab Host (2 mode via tab internal)
  // - Gaji Turunan: 4 halaman terpisah (BPJS Kes, BPJS TK, Koperasi, Serikat)
  // - Lainnya: Single page (1 tab aplikasi)
  // Note: Hutang Pajak & Bukti Potong PPh dipindah ke Menu Pajak
  // -------------------------------------------------------------------------
  {
    id: 'buku-pengawasan',
    label: 'Buku Pengawasan',
    icon: <FileText size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      // ─────────── Validasi Saldo Awal (item #1) ───────────
      // Dashboard agregator Bar Saldo seluruh Buku Pengawasan.
      // Lihat .claude/docs/Booku v2/buku-pengawasan/validasi-saldo-awal.md
      {
        id: 'validasi-saldo-awal',
        label: 'Validasi Saldo Awal',
        icon: <FileText size={16} />,
        path: '/buku-pengawasan/validasi-saldo-awal',
      },
      // ─────────── Bank & Cash ───────────
      {
        id: 'bank-cash',
        label: 'Bank & Cash',
        icon: <Wallet size={18} />,
        children: [
          {
            id: 'buku-kas',
            label: 'Buku Kas',
            icon: <Banknote size={16} />,
            path: '/laporan/buku-kas',
            minLevel: LevelUser_03_Direktur,
          },
          {
            id: 'buku-bank',
            label: 'Buku Bank',
            icon: <Landmark size={16} />,
            path: '/laporan/buku-bank',
            minLevel: LevelUser_03_Direktur,
          },
          {
            id: 'buku-cash-advance',
            label: 'Buku Cash Advance',
            icon: <CreditCard size={16} />,
            path: '/laporan/buku-cash-advance',
            minLevel: LevelUser_03_Direktur,
          },
          {
            id: 'buku-petty-cash',
            label: 'Buku Petty Cash',
            icon: <Coins size={16} />,
            path: '/laporan/buku-petty-cash',
            minLevel: LevelUser_03_Direktur,
          },
          {
            id: 'bank-garansi',
            label: 'Bank Garansi',
            icon: <Landmark size={16} />,
            path: '/buku-pengawasan/bank-garansi',
          },
        ],
      },

      // ─────────── Penerimaan-Pengeluaran ───────────
      {
        id: 'penerimaan-pengeluaran',
        label: 'Penerimaan & Pengeluaran',
        icon: <ArrowLeftRight size={18} />,
        children: [
          {
            id: 'bukti-penerimaan',
            label: 'Bukti Penerimaan Bank/Cash',
            icon: <ArrowDownCircle size={16} />,
            path: '/buku-pengawasan/bukti-penerimaan',
          },
          {
            id: 'bukti-pengeluaran',
            label: 'Bukti Pengeluaran Bank/Cash',
            icon: <ArrowUpCircle size={16} />,
            path: '/buku-pengawasan/bukti-pengeluaran',
          },
          {
            id: 'bundel-pengajuan',
            label: 'Bundel Pengajuan Pengeluaran',
            icon: <ClipboardList size={16} />,
            path: '/buku-pengawasan/bundel-pengajuan',
          },
          {
            id: 'pemindahbukuan',
            label: 'Pemindahbukuan',
            icon: <ArrowLeftRight size={16} />,
            path: '/buku-pengawasan/pemindahbukuan',
          },
        ],
      },

      // ─────────── Gaji ───────────
      // Gaji + 4 Turunan Gaji (masing-masing halaman terpisah di level aplikasi)
      {
        id: 'gaji',
        label: 'Gaji',
        icon: <Users size={18} />,
        children: [
          {
            id: 'buku-gaji',
            label: 'Buku Gaji',
            icon: <Users size={16} />,
            path: '/buku-pengawasan/gaji',
          },
          // 4 Turunan Gaji - masing-masing tab aplikasi terpisah
          {
            id: 'hutang-bpjs-kesehatan',
            label: 'Hutang BPJS Kesehatan',
            icon: <Users size={16} />,
            path: '/buku-pengawasan/hutang-bpjs-kesehatan',
          },
          {
            id: 'hutang-bpjs-ketenagakerjaan',
            label: 'Hutang BPJS Ketenagakerjaan',
            icon: <Users size={16} />,
            path: '/buku-pengawasan/hutang-bpjs-ketenagakerjaan',
          },
          {
            id: 'hutang-koperasi-karyawan',
            label: 'Hutang Koperasi Karyawan',
            icon: <Users size={16} />,
            path: '/buku-pengawasan/hutang-koperasi-karyawan',
          },
          {
            id: 'hutang-serikat',
            label: 'Hutang Serikat',
            icon: <Users size={16} />,
            path: '/buku-pengawasan/hutang-serikat',
          },
        ],
      },

      // ─────────── Hutang ───────────
      // Urutan berdasarkan KodeTautanCOA (termuda → tertua)
      {
        id: 'hutang',
        label: 'Hutang',
        icon: <Receipt size={18} />,
        children: [
          // Hutang Usaha (COA 21101) - Single-Tab Host (10 mode via tab internal)
          {
            id: 'hutang-usaha',
            label: 'Hutang Usaha',
            icon: <Receipt size={16} />,
            path: '/buku-pengawasan/hutang-usaha',
            minLevel: LevelUser_03_Direktur,
          },
          // Hutang Pihak Ketiga (COA 21802)
          {
            id: 'hutang-pihak-ketiga',
            label: 'Hutang Pihak Ketiga',
            icon: <Handshake size={16} />,
            path: '/buku-pengawasan/hutang-pihak-ketiga',
          },
          // Hutang Karyawan (COA 21808)
          {
            id: 'hutang-karyawan',
            label: 'Hutang Karyawan',
            icon: <UserSquare2 size={16} />,
            path: '/buku-pengawasan/hutang-karyawan',
          },
          // Hutang Leasing (COA 22100) - Tab aplikasi terpisah
          {
            id: 'hutang-leasing',
            label: 'Hutang Leasing',
            icon: <CreditCard size={16} />,
            path: '/buku-pengawasan/hutang-leasing',
          },
          // Hutang Bank (COA 23100) - Tab aplikasi terpisah
          {
            id: 'hutang-bank',
            label: 'Hutang Bank',
            icon: <Landmark size={16} />,
            path: '/buku-pengawasan/hutang-bank',
          },
          // Hutang Pemegang Saham (COA 24100)
          {
            id: 'hutang-pemegang-saham',
            label: 'Hutang Pemegang Saham',
            icon: <Briefcase size={16} />,
            path: '/buku-pengawasan/hutang-pemegang-saham',
          },
          // Hutang Afiliasi (COA 25100)
          {
            id: 'hutang-afiliasi',
            label: 'Hutang Afiliasi',
            icon: <Building2 size={16} />,
            path: '/buku-pengawasan/hutang-afiliasi',
          },
          // Hutang Dividen (COA 29999)
          {
            id: 'hutang-dividen',
            label: 'Hutang Dividen',
            icon: <Coins size={16} />,
            path: '/buku-pengawasan/hutang-dividen',
          },
        ],
      },

      // ─────────── Piutang ───────────
      // Urutan berdasarkan KodeTautanCOA (termuda → tertua)
      {
        id: 'piutang',
        label: 'Piutang',
        icon: <Wallet size={18} />,
        children: [
          // Piutang Usaha (COA 11501) - Single-Tab Host (10 mode via tab internal)
          {
            id: 'piutang-usaha',
            label: 'Piutang Usaha',
            icon: <Receipt size={16} />,
            path: '/buku-pengawasan/piutang-usaha',
          },
          // Piutang Afiliasi (COA 11503)
          {
            id: 'piutang-afiliasi',
            label: 'Piutang Afiliasi',
            icon: <Building2 size={16} />,
            path: '/buku-pengawasan/piutang-afiliasi',
          },
          // Piutang Pihak Ketiga (COA 11504)
          {
            id: 'piutang-pihak-ketiga',
            label: 'Piutang Pihak Ketiga',
            icon: <Handshake size={16} />,
            path: '/buku-pengawasan/piutang-pihak-ketiga',
          },
          // Piutang Pemegang Saham (COA 11505)
          {
            id: 'piutang-pemegang-saham',
            label: 'Piutang Pemegang Saham',
            icon: <Briefcase size={16} />,
            path: '/buku-pengawasan/piutang-pemegang-saham',
          },
          // Piutang Dividen (COA 11506)
          {
            id: 'piutang-dividen',
            label: 'Piutang Dividen',
            icon: <Coins size={16} />,
            path: '/buku-pengawasan/piutang-dividen',
          },
          // Piutang Karyawan (COA 11508)
          {
            id: 'piutang-karyawan',
            label: 'Piutang Karyawan',
            icon: <UserSquare2 size={16} />,
            path: '/buku-pengawasan/piutang-karyawan',
          },
          // Deposit Operasional (COA 11608)
          {
            id: 'deposit-operasional',
            label: 'Deposit Operasional',
            icon: <Coins size={16} />,
            path: '/buku-pengawasan/deposit-operasional',
          },
        ],
      },

      // ─────────── Aktiva Lainnya ───────────
      {
        id: 'aktiva-lainnya',
        label: 'Aktiva Lainnya',
        icon: <Package size={18} />,
        path: '/buku-pengawasan/aktiva-lainnya',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // STOCK OPNAME
  // Single-Tab Host: 5 mode via tab internal (semua dari class wpfUsc_StockOpname)
  // Mode: Bahan Penolong, Bahan Baku, Barang Jadi, Barang Dalam Proses (Cek Fisik), Barang Dalam Proses (Tarikan Data)
  // Khusus perusahaan Manufaktur
  // -------------------------------------------------------------------------
  {
    id: 'stock-opname',
    label: 'Stock Opname',
    icon: <Package size={20} />,
    requiresTahunBukuNormal: true,
    requiresManufaktur: true,
    path: '/stock-opname',
  },

  // -------------------------------------------------------------------------
  // MANAJEMEN ASSET
  // Single Pages: tiap halaman = 1 tab aplikasi (class berbeda)
  // -------------------------------------------------------------------------
  {
    id: 'manajemen-asset',
    label: 'Manajemen Asset',
    icon: <Briefcase size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      // Asset Tetap
      {
        id: 'penyusutan-asset',
        label: 'Daftar Penyusutan Asset Tetap',
        icon: <FileText size={18} />,
        path: '/manajemen-asset/penyusutan',
      },
      {
        id: 'disposal-asset',
        label: 'Buku Disposal Asset Tetap',
        icon: <FileText size={18} />,
        path: '/manajemen-asset/disposal',
      },
      // Amortisasi
      {
        id: 'amortisasi-biaya',
        label: 'Daftar Amortisasi Biaya',
        icon: <FileText size={18} />,
        path: '/manajemen-asset/amortisasi',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // JURNAL
  // Posisi di akhir section Data & Transaksi karena merupakan "muara" dari
  // semua transaksi operasional (Pembelian, Penjualan, Buku Pengawasan, dll)
  // -------------------------------------------------------------------------
  {
    id: 'jurnal',
    label: 'Jurnal',
    icon: <BookOpen size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      {
        id: 'jurnal-umum',
        label: 'Jurnal Umum',
        icon: <FileText size={18} />,
        path: '/jurnal/umum',
        minLevel: LevelUser_03_Direktur,
      },
      {
        // Adjustment Akhir Bulan — konsolidasi 4 jenis adjustment (Penyusutan,
        // Amortisasi, Forex, HPP) dalam 1 halaman 4-section vertikal.
        // Menggantikan 4 submenu nested lama (placeholder).
        // Hanya tersedia di Tahun Buku NORMAL.
        // Lihat .claude/docs/Booku v2/jurnal-akuntansi/adjustment-akhir-bulan.md
        id: 'adjustment-akhir-bulan',
        label: 'Adjustment Akhir Bulan',
        icon: <FileText size={18} />,
        path: '/jurnal/adjustment-akhir-bulan',
        minLevel: LevelUser_03_Direktur,
      },
    ],
  },

  // -------------------------------------------------------------------------
  // BUKU BESAR (menu utama - leaf, langsung buka halaman)
  // Ringkasan akhir dari semua jurnal per akun COA
  // -------------------------------------------------------------------------
  {
    id: 'buku-besar',
    label: 'Buku Besar',
    icon: <BookOpen size={20} />,
    path: '/laporan/buku-besar',
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // -------------------------------------------------------------------------
  // PAJAK (Section 11 di Booku Lama)
  // Modul Pajak terpusat - semua fitur pajak ada di sini:
  // - Profil, Perhitungan Bulanan, Equalisasi Tahunan
  // - Hutang Pajak (PPh 21/22/23/4(2)/25/26/29, PPN, Ketetapan, Impor)
  // - Input Dokumen Perpajakan (PBk, Ketetapan)
  // - Bukti Potong PPh (Prepaid/Paid)
  // -------------------------------------------------------------------------
  {
    id: 'pajak',
    label: 'Pajak',
    icon: <Receipt size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      // ─────────── Profil & Perhitungan ───────────
      {
        id: 'profil-pajak',
        label: 'Profil Pajak Perusahaan',
        icon: <Building2 size={18} />,
        path: '/pajak/profil',
      },
      {
        id: 'perhitungan-pajak-bulanan',
        label: 'Perhitungan Pajak Bulanan',
        icon: <Calculator size={18} />,
        path: '/pajak/perhitungan-bulanan',
      },

      // ─────────── Hutang Pajak ───────────
      {
        id: 'pajak-hutang-pajak',
        label: 'Hutang Pajak',
        icon: <Receipt size={18} />,
        children: [
          {
            id: 'pajak-pph-21',
            label: 'PPh Pasal 21',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-21',
          },
          {
            id: 'pajak-pph-22',
            label: 'PPh Pasal 22',
            icon: <Receipt size={16} />,
            children: [
              {
                id: 'pajak-pph-22-lokal',
                label: 'Lokal',
                icon: <Receipt size={14} />,
                path: '/pajak/pph-22-lokal',
              },
              {
                id: 'pajak-pph-22-impor',
                label: 'Impor',
                icon: <Receipt size={14} />,
                path: '/pajak/pph-22-impor',
              },
            ],
          },
          {
            id: 'pajak-pph-23',
            label: 'PPh Pasal 23',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-23',
          },
          {
            id: 'pajak-pph-42',
            label: 'PPh Pasal 4(2)',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-42',
          },
          {
            id: 'pajak-pph-25',
            label: 'PPh Pasal 25',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-25',
          },
          {
            id: 'pajak-pph-26',
            label: 'PPh Pasal 26',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-26',
          },
          {
            id: 'pajak-pph-29',
            label: 'PPh Pasal 29',
            icon: <Receipt size={16} />,
            path: '/pajak/pph-29',
          },
          {
            id: 'pajak-ppn',
            label: 'PPN',
            icon: <Receipt size={16} />,
            path: '/pajak/ppn',
          },
          {
            id: 'pajak-ketetapan',
            label: 'Ketetapan Pajak',
            icon: <Receipt size={16} />,
            path: '/pajak/ketetapan',
          },
          {
            id: 'pajak-impor',
            label: 'Pajak Impor',
            icon: <Receipt size={16} />,
            path: '/pajak/impor',
          },
        ],
      },

      // ─────────── Input Dokumen Perpajakan ───────────
      {
        id: 'input-dokumen-perpajakan',
        label: 'Input Dokumen Perpajakan',
        icon: <FileText size={18} />,
        children: [
          {
            id: 'input-bukti-pbk',
            label: 'Input Bukti PBk',
            icon: <FileText size={16} />,
            path: '/pajak/input-pbk',
          },
          {
            id: 'input-ketetapan-pajak',
            label: 'Input Ketetapan Pajak',
            icon: <FileText size={16} />,
            path: '/pajak/input-ketetapan',
          },
        ],
      },

      // ─────────── Equalisasi & Bukti Potong ───────────
      {
        id: 'equalisasi-pajak',
        label: 'Perhitungan Equalisasi Pajak Tahunan',
        icon: <Calculator size={18} />,
        path: '/pajak/equalisasi',
      },
      {
        id: 'pajak-bukti-potong-paid',
        label: 'Bukti Potong PPh (Paid)',
        icon: <FileText size={18} />,
        path: '/pajak/bukti-potong/paid',
      },
      {
        id: 'pajak-bukti-potong-prepaid',
        label: 'Bukti Potong PPh (Prepaid)',
        icon: <FileText size={18} />,
        path: '/pajak/bukti-potong/prepaid',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // LAPORAN
  // Struktur: Neraca Lajur, Laporan Keuangan (HPP, Laba Rugi, Neraca),
  //           Laporan Aktivitas Transaksi
  // -------------------------------------------------------------------------
  {
    id: 'laporan',
    label: 'Laporan',
    icon: <PieChart size={20} />,
    requiresTahunBukuNormal: true,
    children: [
      {
        id: 'neraca-lajur',
        label: 'Neraca Lajur',
        icon: <FileText size={18} />,
        path: '/laporan/neraca-lajur',
      },
      {
        id: 'laporan-keuangan',
        label: 'Laporan Keuangan',
        icon: <FileText size={18} />,
        children: [
          {
            id: 'laporan-hpp',
            label: 'Laporan HPP',
            icon: <FileText size={16} />,
            path: '/laporan/hpp',
          },
          {
            id: 'laba-rugi-bulanan',
            label: 'Laba Rugi Bulanan',
            icon: <DollarSign size={16} />,
            path: '/laporan/laba-rugi-bulanan',
          },
          {
            id: 'neraca-bulanan',
            label: 'Neraca Bulanan',
            icon: <FileText size={16} />,
            path: '/laporan/neraca-bulanan',
          },
        ],
      },
      {
        id: 'laporan-aktivitas-transaksi',
        label: 'Laporan Aktivitas Transaksi',
        icon: <FileText size={18} />,
        path: '/laporan/aktivitas-transaksi',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PENGATURAN
  // -------------------------------------------------------------------------
  {
    id: 'pengaturan',
    label: 'Pengaturan',
    icon: <Settings size={20} />,
    children: [
      {
        id: 'company-profile',
        label: 'Company Profile',
        icon: <Briefcase size={18} />,
        path: '/pengaturan/company-profile',
        minLevel: LevelUser_03_Direktur,
      },
      {
        id: 'pengaturan-umum',
        label: 'Pengaturan Umum',
        icon: <Settings size={18} />,
        path: '/pengaturan/umum',
      },
      {
        id: 'pengaturan-database',
        label: 'Pengaturan Database',
        icon: <Settings size={18} />,
        path: '/pengaturan/database',
      },
    ],
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export interface JenisUsaha {
  dagang: boolean;
  manufaktur: boolean;
  jasa: boolean;
}

/**
 * Filter menu items berdasarkan level user, jenis usaha, dan jenis tahun buku
 */
export function filterMenuConfigItems(
  items: MenuConfigItem[],
  userLevel: number,
  jenisUsaha: JenisUsaha,
  isTahunBukuNormal: boolean
): MenuConfigItem[] {
  return items
    .filter((item) => {
      // Check minLevel
      if (item.minLevel !== undefined && userLevel < item.minLevel) {
        return false;
      }

      // Check jenis usaha
      if ('requiresDagang' in item && item.requiresDagang && !jenisUsaha.dagang) {
        return false;
      }
      if ('requiresManufaktur' in item && item.requiresManufaktur && !jenisUsaha.manufaktur) {
        return false;
      }
      if ('requiresJasa' in item && item.requiresJasa && !jenisUsaha.jasa) {
        return false;
      }

      // Check tahun buku
      if ('requiresTahunBukuNormal' in item && item.requiresTahunBukuNormal && !isTahunBukuNormal) {
        return false;
      }
      // requiresTahunBukuLampau = hanya tampil jika BUKAN tahun buku normal (tahun pertama)
      if ('requiresTahunBukuLampau' in item && item.requiresTahunBukuLampau && isTahunBukuNormal) {
        return false;
      }

      return true;
    })
    .map((item) => {
      // Jika branch, filter children juga
      if (isMenuBranch(item)) {
        const filteredChildren = filterMenuConfigItems(
          item.children,
          userLevel,
          jenisUsaha,
          isTahunBukuNormal
        );
        // Jika tidak ada children setelah filter, kembalikan null
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter((item): item is MenuConfigItem => item !== null);
}

/**
 * Filter main menu items
 */
export function filterMainMenuItems(
  items: MainMenuItem[],
  userLevel: number,
  jenisUsaha: JenisUsaha,
  isTahunBukuNormal: boolean
): MainMenuItem[] {
  return items
    .filter((item) => {
      // Check minLevel
      if (item.minLevel !== undefined && userLevel < item.minLevel) {
        return false;
      }

      // Check jenis usaha
      if (item.requiresDagang && !jenisUsaha.dagang) {
        return false;
      }
      if (item.requiresManufaktur && !jenisUsaha.manufaktur) {
        return false;
      }
      if (item.requiresJasa && !jenisUsaha.jasa) {
        return false;
      }

      // Check tahun buku
      if (item.requiresTahunBukuNormal && !isTahunBukuNormal) {
        return false;
      }
      // requiresTahunBukuLampau = hanya tampil jika BUKAN tahun buku normal (tahun pertama)
      if (item.requiresTahunBukuLampau && isTahunBukuNormal) {
        return false;
      }

      return true;
    })
    .map((item) => {
      // Jika punya children, filter children juga
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuConfigItems(
          item.children,
          userLevel,
          jenisUsaha,
          isTahunBukuNormal
        );
        // Jika tidak ada children setelah filter, kembalikan null
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter((item): item is MainMenuItem => item !== null);
}
